import { useEffect, useRef, useState } from 'react';

/**
 * Reveal streamed text at the display refresh rate so large transport chunks
 * animate in as continuous typing rather than snapping into place. When not
 * streaming (or when the text shrinks) it renders the full text immediately.
 */
export function useSmoothStreamingText(text: string, isStreaming: boolean): string {
  const [visibleText, setVisibleText] = useState(text);
  const visibleLengthRef = useRef(text.length);
  const targetRef = useRef(text);

  useEffect(() => {
    targetRef.current = text;

    if (!isStreaming || text.length < visibleLengthRef.current) {
      visibleLengthRef.current = text.length;
      setVisibleText(text);
      return;
    }

    let frame = 0;
    const reveal = () => {
      const remaining = targetRef.current.length - visibleLengthRef.current;
      if (remaining <= 0) return;

      // Catch up quickly after a large transport chunk while keeping ordinary
      // token-sized updates visually continuous at the display refresh rate.
      const step = Math.min(24, Math.max(1, Math.ceil(remaining / 5)));
      visibleLengthRef.current += step;
      setVisibleText(targetRef.current.slice(0, visibleLengthRef.current));
      frame = window.requestAnimationFrame(reveal);
    };

    frame = window.requestAnimationFrame(reveal);
    return () => window.cancelAnimationFrame(frame);
  }, [isStreaming, text]);

  return visibleText;
}
